<template>
  <section class="atlas-page">
    <div class="atlas-shell">
      <div class="topbar">
        <div class="crumbs">
          <button type="button" class="nav-chip" @click="goPortal">{{ t("back") }}</button>
        </div>
        <div class="lang-pill">
          <button
            v-for="option in languages"
            :key="option"
            type="button"
            :class="['lang-chip', { active: option === lang }]"
            @click="setLang(option)"
          >
            {{ option.toUpperCase() }}
          </button>
        </div>
      </div>

      <p v-if="loading" class="state-card">{{ t("loading") }}</p>
      <p v-else-if="errorMessage" class="error-panel">{{ errorMessage }}</p>

      <template v-else-if="series">
        <header class="hero-card panel-card">
          <div class="hero-copy">
            <p class="eyebrow">{{ t("eyebrow") }}</p>
            <h1>{{ pickLangText(series.title, lang) }}</h1>
            <p class="lead">{{ pickLangText(series.summary, lang) }}</p>
          </div>

          <aside class="hero-meta">
            <div class="meta-box">
              <span class="meta-label">{{ t("type") }}</span>
              <strong>{{ getCategoryLabel(series.seriesType, lang) }}</strong>
            </div>
            <div class="meta-box">
              <span class="meta-label">{{ t("planned") }}</span>
              <strong>{{ series.plannedEpisodeCount ?? '-' }}</strong>
            </div>
            <div class="meta-box">
              <span class="meta-label">{{ t("status") }}</span>
              <strong>{{ getStatusLabel(series.status, lang) }}</strong>
            </div>
            <div class="meta-box wide">
              <span class="meta-label">Slug</span>
              <strong>{{ series.slug }}</strong>
            </div>
          </aside>
        </header>

        <section class="panel-card episode-board">
          <div class="section-head">
            <div>
              <p class="section-tag">{{ t("sectionTag") }}</p>
              <h2>{{ t("episodes") }}</h2>
            </div>
            <span>{{ series.topics.length }} {{ t("items") }}</span>
          </div>

          <div v-if="!series.topics.length" class="empty-state">
            {{ t("empty") }}
          </div>

          <div v-else class="episode-grid">
            <article
              v-for="topic in series.topics"
              :key="topic.topicId"
              class="episode-card"
              @click="openTopic(topic.slug)"
            >
              <div class="card-topline">
                <span class="soft-badge">{{ topic.episodeNo ? `EP ${topic.episodeNo}` : getCategoryLabel(topic.topicType, lang) }}</span>
                <span class="status-chip">{{ getStatusLabel(topic.status, lang) }}</span>
              </div>
              <h3>{{ pickLangText(topic.title, lang) }}</h3>
              <p>{{ summarizeText(topic.summary, lang, 190) }}</p>
              <div class="meta-line">
                <span>{{ formatDate(topic.publishedAt) }}</span>
                <span>{{ topic.slug }}</span>
              </div>
            </article>
          </div>
        </section>
      </template>
    </div>
  </section>
</template>

<script setup>
import axios from "axios";
import { onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  API_BASE,
  ensureApiObject,
  formatDate,
  getCategoryLabel,
  getStatusLabel,
  pickLangText,
  summarizeText,
} from "./portalShared";
import { useSiteLanguage } from "../../store/siteLanguage";

const route = useRoute();
const router = useRouter();
const { lang, languages, setLang } = useSiteLanguage();

const copy = {
  ja: {
    back: "ポータルへ戻る",
    loading: "シリーズを読込中...",
    eyebrow: "SERIES CHRONICLE",
    type: "分類",
    planned: "予定集数",
    status: "状態",
    sectionTag: "EPISODE INDEX",
    episodes: "Episodes",
    items: "件",
    empty: "まだシリーズ内のトピックがありません。",
  },
  zh: {
    back: "返回门户",
    loading: "正在读取系列...",
    eyebrow: "SERIES CHRONICLE",
    type: "分类",
    planned: "计划集数",
    status: "状态",
    sectionTag: "EPISODE INDEX",
    episodes: "Episodes",
    items: "条",
    empty: "这个系列里还没有分集内容。",
  },
  en: {
    back: "Back to portal",
    loading: "Loading series...",
    eyebrow: "SERIES CHRONICLE",
    type: "Type",
    planned: "Planned",
    status: "Status",
    sectionTag: "EPISODE INDEX",
    episodes: "Episodes",
    items: "items",
    empty: "This series does not have any topic pages yet.",
  },
};

function t(key) {
  return copy[lang.value]?.[key] || key;
}

const series = ref(null);
const loading = ref(true);
const errorMessage = ref("");

async function loadSeries() {
  loading.value = true;
  errorMessage.value = "";

  try {
    const response = await axios.get(`${API_BASE}/api/kendou/series/${encodeURIComponent(route.params.slug)}`, {
      timeout: 10000,
    });
    series.value = ensureApiObject(response, "Kendou series API");
  } catch (error) {
    console.error(error);
    series.value = null;
    errorMessage.value = error?.response?.status === 404 ? "Series not found." : error?.message || "Failed to load series.";
  } finally {
    loading.value = false;
  }
}

function goPortal() {
  router.push("/KendouPage/terminology");
}

function openTopic(slug) {
  router.push(`/KendouPage/topic/${encodeURIComponent(slug)}`);
}

watch(() => route.params.slug, loadSeries);
onMounted(loadSeries);
</script>

<style scoped>
.atlas-page {
  --paper: rgba(251, 245, 235, 0.92);
  --paper-strong: rgba(255, 250, 242, 0.98);
  --ink: #2f2116;
  --ink-soft: #5d4b3d;
  --line: rgba(182, 148, 110, 0.34);
  min-height: 100vh;
  padding: 20px;
  background:
    radial-gradient(circle at 16% 12%, rgba(255, 255, 255, 0.7), transparent 28%),
    radial-gradient(circle at 86% 82%, rgba(235, 190, 116, 0.28), transparent 26%),
    linear-gradient(135deg, #e7d8bf 0%, #efe6d3 24%, #f5eedf 48%, #eadab8 100%);
}

.atlas-shell {
  max-width: 1320px;
  margin: 0 auto;
  display: grid;
  gap: 18px;
}

.topbar,
.crumbs,
.section-head,
.card-topline,
.meta-line {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: space-between;
}

.lang-pill {
  display: inline-flex;
  gap: 8px;
  padding: 8px;
  border: 1px solid rgba(191, 169, 139, 0.72);
  border-radius: 999px;
  background: rgba(255, 250, 245, 0.8);
  box-shadow: 0 10px 24px rgba(106, 75, 41, 0.08);
}

.lang-chip,
.nav-chip {
  border-radius: 999px;
  cursor: pointer;
}

.lang-chip {
  border: 1px solid transparent;
  background: transparent;
  color: #604d3d;
  font-family: "Cormorant Garamond", Georgia, serif;
  font-size: 1rem;
  padding: 10px 16px;
  transition: transform 0.22s ease, box-shadow 0.22s ease, background-color 0.22s ease, color 0.22s ease, border-color 0.22s ease;
}

.lang-chip.active {
  background: linear-gradient(135deg, #b64926 0%, #8a2613 100%);
  color: #fff8f3;
}

.lang-chip:hover {
  transform: translateY(-2px) scale(1.02);
  box-shadow: 0 10px 20px rgba(115, 80, 43, 0.14);
}

.lang-chip:active {
  transform: translateY(0) scale(0.98);
}

.nav-chip {
  border: 1px solid rgba(177, 144, 108, 0.7);
  background: rgba(255, 249, 240, 0.88);
  color: #5b4636;
  padding: 10px 15px;
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease, background-color 0.22s ease, opacity 0.22s ease;
}

.crumbs:hover .nav-chip {
  opacity: 0.84;
  transform: translateX(3px);
}

.crumbs:hover .nav-chip:hover {
  opacity: 1;
  transform: translateX(-3px) translateY(-2px);
}

.nav-chip:hover {
  border-color: rgba(184, 94, 55, 0.62);
  background: rgba(255, 252, 246, 0.98);
  box-shadow: 0 12px 24px rgba(105, 74, 40, 0.12);
}

.nav-chip:active {
  transform: translateX(0) translateY(0) scale(0.985);
}

.panel-card,
.state-card,
.error-panel,
.episode-card {
  border: 1px solid var(--line);
  border-radius: 28px;
  background: linear-gradient(180deg, var(--paper-strong) 0%, var(--paper) 100%);
  box-shadow:
    0 22px 44px rgba(118, 87, 49, 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.75);
}

.hero-card,
.episode-board {
  padding: 20px 22px 22px;
}

.hero-card {
  display: grid;
  grid-template-columns: 1.35fr 0.85fr;
  gap: 18px;
}

.eyebrow,
.section-tag {
  margin: 0 0 10px;
  font-family: "Cormorant Garamond", "Times New Roman", serif;
  font-size: 14px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #9c341d;
}

.hero-card h1,
.section-head h2,
.episode-card h3 {
  margin: 0;
  color: var(--ink);
}

.hero-card h1 {
  font-family: "Noto Serif JP", "Yu Mincho", Georgia, serif;
  font-size: clamp(2.4rem, 4.2vw, 4rem);
  line-height: 1.1;
}

.lead,
.hero-meta,
.episode-card p,
.meta-line,
.section-head span,
.state-card,
.error-panel {
  color: var(--ink-soft);
  line-height: 1.8;
  white-space: pre-line;
}

.hero-meta {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  align-content: start;
}

.meta-box {
  border: 1px solid rgba(216, 180, 139, 0.58);
  border-radius: 20px;
  background: rgba(255, 248, 239, 0.82);
  padding: 16px;
  display: grid;
  gap: 6px;
  transition: transform 0.26s ease, opacity 0.26s ease, filter 0.26s ease, box-shadow 0.26s ease, border-color 0.26s ease;
}

.meta-box.wide {
  grid-column: 1 / -1;
}

.meta-label {
  font-size: 0.8rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #7a5d44;
}

.meta-box strong {
  color: var(--ink);
  font-family: "Cormorant Garamond", Georgia, serif;
  font-size: 1.4rem;
}

.episode-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.episode-card {
  padding: 16px 18px 18px;
  cursor: pointer;
  transition: transform 0.24s ease, box-shadow 0.24s ease, border-color 0.24s ease, opacity 0.24s ease, filter 0.24s ease;
}

.episode-card:hover {
  transform: translateY(-2px);
  border-color: rgba(184, 94, 55, 0.65);
}

.hero-meta:hover .meta-box,
.episode-grid:hover .episode-card {
  opacity: 0.74;
  transform: translateY(5px) scale(0.992);
  filter: saturate(0.86);
}

.hero-meta:hover .meta-box:hover,
.episode-grid:hover .episode-card:hover {
  opacity: 1;
  transform: translateY(-6px) scale(1.012);
  filter: saturate(1.04);
  border-color: rgba(184, 94, 55, 0.65);
  box-shadow:
    0 18px 30px rgba(117, 78, 40, 0.16),
    inset 0 1px 0 rgba(255, 255, 255, 0.75);
}

.soft-badge,
.status-chip {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 6px 11px;
  font-size: 0.78rem;
  letter-spacing: 0.06em;
}

.soft-badge {
  background: rgba(235, 220, 201, 0.9);
  color: #6d5138;
}

.status-chip {
  background: linear-gradient(135deg, #442616 0%, #24130b 100%);
  color: #fff7f0;
}

.empty-state,
.state-card,
.error-panel {
  padding: 16px 18px;
}

@media (max-width: 980px) {
  .hero-card,
  .episode-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 760px) {
  .atlas-page {
    padding: 14px;
  }

  .topbar {
    flex-direction: column;
    align-items: flex-start;
  }

  .lang-pill {
    align-self: flex-start;
  }

  .hero-meta {
    grid-template-columns: 1fr;
  }
}

@keyframes atlasFadeLift {
  from {
    opacity: 0;
    transform: translateY(28px) scale(0.985);
    filter: blur(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
    filter: blur(0);
  }
}

.hero-card,
.episode-board,
.state-card,
.error-panel,
.meta-box,
.episode-card {
  animation: atlasFadeLift 0.72s cubic-bezier(0.2, 0.8, 0.2, 1) both;
  will-change: transform, opacity, filter;
}

.hero-card {
  animation-delay: 0.06s;
}

.episode-board {
  animation-delay: 0.18s;
}

.hero-meta .meta-box:nth-child(1) {
  animation-delay: 0.16s;
}

.hero-meta .meta-box:nth-child(2) {
  animation-delay: 0.22s;
}

.hero-meta .meta-box:nth-child(3) {
  animation-delay: 0.28s;
}

.hero-meta .meta-box:nth-child(4) {
  animation-delay: 0.34s;
}

.episode-grid .episode-card:nth-child(1) {
  animation-delay: 0.24s;
}

.episode-grid .episode-card:nth-child(2) {
  animation-delay: 0.3s;
}

.episode-grid .episode-card:nth-child(3) {
  animation-delay: 0.36s;
}

.episode-grid .episode-card:nth-child(4) {
  animation-delay: 0.42s;
}

.episode-grid .episode-card:nth-child(5) {
  animation-delay: 0.48s;
}

.episode-grid .episode-card:nth-child(6) {
  animation-delay: 0.54s;
}

@media (prefers-reduced-motion: reduce) {
  .hero-card,
  .episode-board,
  .state-card,
  .error-panel,
  .meta-box,
  .episode-card,
  .lang-chip,
  .nav-chip {
    animation: none;
    transition: none;
  }
}
</style>
